import React, { useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { MUIDatePicker } from 'components/FormHelpers/DatePicker';
import atoms from 'ctsutheme/components/atoms';
import { ValidatorForm } from 'react-material-ui-form-validator';
import {
  getUIDateFormat, getApiDateFormat, useCtxValue, GetFromCtxValue
} from 'helpers';
import { compose, withHandlers, withState } from 'recompose';
import { openConfirmation } from 'helpers/Confirmation';
import _ from 'lodash';
import { FormViewContent } from 'components/FormViewContent';
import PropTypes from 'prop-types';
import { withAppContext } from '@ctsu/loader';
import { CTSUCardActions } from 'components';

const { Tooltip, Divider } = atoms;

const styles = theme => ({
  textField: {
    textIndent: 5,
    width: 150,
    textAlign: 'left'
  }
});

const PCDUpdate = props => {
  const {
    classes,
    state,
    grpProtStatusDate,
    completionDates,
    grpProtStatus,
    setState,
    handleDateChange,
    cancelPcd,
    savePcd,
    clearPCD,
    multiPhase
  } = props;

  useEffect(() => {
    setState({
      ...completionDates
    });

    const dateObj = new Date();
    dateObj.setHours(0, 0, 0, 0);
    ValidatorForm.addValidationRule('minDate', value => value && value !== 'Invalid date' && value >= dateObj);
    ValidatorForm.addValidationRule('isApprovedCheck', value => (
      value
        && value !== 'Invalid date'
        && grpProtStatusDate
        && value > getUIDateFormat(grpProtStatusDate)
        && grpProtStatus !== 'Complete'
    ));
  }, [completionDates, grpProtStatus, grpProtStatusDate, setState]);

  const { anticipatedPrimaryComplDate, actualPrimaryComplDate, phaseTransitionDate } = state;

  const disableAPCD = !_.isEmpty(actualPrimaryComplDate) || grpProtStatus === 'Complete';

  return (
    <ValidatorForm onSubmit={savePcd}>
      <FormViewContent
        className="MuiListItem-react-datepicker"
        primaryText="Anticipated Primary Completion Date"
        secondaryText={(
          <Tooltip
            placement="right-end"
            title={useCtxValue('PROTOCOL_APCD_TOOLTIP')}
          >
            <MUIDatePicker
              title="Anticipated Primary Completion Date"
              id="date-anticipate"
              name="anticipatedPrimaryComplDate"
              variant="outlined"
              value={getUIDateFormat(anticipatedPrimaryComplDate)}
              className={classes.textField}
              onChange={handleDateChange('anticipatedPrimaryComplDate')}
              disabled={disableAPCD}
              validators={
                !disableAPCD ? ['isApprovedCheck', 'minDate'] : []
              }
              errorMessages={
                !disableAPCD
                  ? [
                    `Provide a date which is later than ${
                      grpProtStatusDate}`,
                    'Anticipated Primary Completion Date cannot be a past date.'
                  ]
                  : []
              }
            />
          </Tooltip>
        )}
      />
      <Divider />
      <FormViewContent
        className="MuiListItem-react-datepicker"
        primaryText="Primary Completion Date"
        secondaryText={(
          <Tooltip
            placement="right-end"
            title={useCtxValue('PROTOCOL_PCD_TOOLTIP')}
          >
            <MUIDatePicker
              title="Primary Completion Date"
              id="date-primary"
              name="actualPrimaryComplDate"
              variant="outlined"
              value={getUIDateFormat(actualPrimaryComplDate)}
              className={classes.textField}
              onChange={handleDateChange('actualPrimaryComplDate')}
              maxDate={new Date()}
            />
          </Tooltip>
        )}
      />
      { multiPhase && (
        <>
          <Divider />
          <FormViewContent
            className="MuiListItem-react-datepicker"
            primaryText={<>Subsequent Phase Activation Date<br /> (For Multi-Phase Trials)</>}
            secondaryText={(
              <Tooltip
                placement="right-end"
                title="Date the second phase of multi-phase trial opened or will open to accrual"
              >
                <MUIDatePicker
                  title="Subsequent Phase Activation Date"
                  id="date-primary"
                  name="phaseTransitionDate"
                  variant="outlined"
                  value={getUIDateFormat(phaseTransitionDate)}
                  className={classes.textField}
                  onChange={handleDateChange('phaseTransitionDate')}
                />
              </Tooltip>
            )}
          />
        </>
      )}
      <Divider />
      <CTSUCardActions
        clearClick={clearPCD}
        cancelClick={cancelPcd}
        disabled={_.isEqual(state, completionDates)}
      />
    </ValidatorForm>
  );
};

PCDUpdate.propTypes = {
  classes: PropTypes.oneOfType([PropTypes.object]),
  state: PropTypes.oneOfType([PropTypes.object]),
  setState: PropTypes.func,
  grpProtStatusDate: PropTypes.string,
  grpProtStatus: PropTypes.string,
  handleDateChange: PropTypes.func,
  completionDates: PropTypes.oneOfType([PropTypes.object]),
  cancelPcd: PropTypes.func,
  savePcd: PropTypes.func,
  clearPCD: PropTypes.func
};

PCDUpdate.defaultProps = {
  classes: null,
  state: null,
  grpProtStatusDate: '',
  completionDates: null,
  grpProtStatus: '',
  setState: null,
  handleDateChange: null,
  cancelPcd: null,
  savePcd: null,
  clearPCD: null
};

const EditPCD = withStyles(styles)(
  compose(
    withState('state', 'setState', {}),
    withHandlers({
      clearPCD: props => event => {
        const { setState, completionDates } = props;
        setState({ ...completionDates });
      },
      savePcd: props => event => {
        const { onSetPcd, state, context } = props;
        const confirmSave = () => {
          onSetPcd(state);
        };

        const checkMessage = () => {
          const { actualPrimaryComplDate, phaseTransitionDate } = props.completionDates
          if (state.actualPrimaryComplDate !== actualPrimaryComplDate) {
            return GetFromCtxValue(context, 'PROTOCOL_PCD_CONFIRMATION');
          }
          if (state.phaseTransitionDate !== phaseTransitionDate) {
            return 'Do you want to save the Subsequent Phase Activation Date?';
          }
          return GetFromCtxValue(context, 'PROTOCOL_APCD_CONFIRM');
        }
        openConfirmation({
          message: checkMessage(),
          onAction: confirmSave,
          name: 'pcd'
        });
      },
      cancelPcd: props => event => {
        const { pcdStatusChange, setState } = props;
        setState({});
        pcdStatusChange(false);
      },
      handleDateChange: props => name => event => {
        const { state, setState } = props;
        state[name] = getApiDateFormat(event);
        setState({ ...state });
      }
    })
  )(PCDUpdate)
);

export default withAppContext(EditPCD);
